// TYPES
import isEqual from 'lodash/isEqual';
import type { TRootState } from 'slices/store';
import { EFieldType } from '@timeedit/types/lib/enums/field.enum';
import type { TType } from '@timeedit/types/lib/types';
import type { TField } from '@timeedit/types/lib/types/field.type';
import type {
  TTEFieldValue,
  TTEObject,
} from '@timeedit/types/lib/types/object.type';
import { isFieldSaveable } from './fieldUtils';

export const NEW_OBJECT_ID = 0;

const categoryValues = (
  fields: TTEFieldValue[] | undefined,
  fieldId: number
): string[] =>
  (fields?.find((field) => field.fieldId === fieldId)?.values ?? [])
    .filter((value) => value != null && value !== '')
    .map(String)
    .sort();

export const getChangedCategoryFieldIds = (
  object: Pick<TTEObject, 'fields'>,
  original: Pick<TTEObject, 'fields'> | undefined,
  fieldMap: Record<number, TField | undefined>
): number[] => {
  const fieldIds = new Set(
    [...(original?.fields ?? []), ...(object.fields ?? [])].map(
      (field) => field.fieldId
    )
  );

  return [...fieldIds].filter((fieldId) => {
    const field = fieldMap[fieldId];
    return (
      field?.fieldType === EFieldType.CATEGORY &&
      field.editable === true &&
      !isEqual(
        categoryValues(original?.fields, fieldId),
        categoryValues(object.fields, fieldId)
      )
    );
  });
};

export const isFieldOnObject = (allFields: TTEFieldValue[], id: number) => {
  let existing = false;
  allFields.forEach((f: TTEFieldValue) => {
    if (f.fieldId === id) {
      existing = true;
    }
  });
  return existing;
};

export const isFieldIdOnObject = (allFields: number[], id: number) => {
  let existing = false;
  allFields.forEach((f: number) => {
    if (f === id) {
      existing = true;
    }
  });
  return existing;
};

export const getNonReferenceFields = (
  fields: TField[],
  type: TType | undefined
) => {
  if (type !== undefined) {
    const parentsOnType: number[] = type?.parentFields;
    const fieldsOnType: number[] = type?.fields;
    const allFields: number[] = parentsOnType?.concat(fieldsOnType);
    const getFieldType = (fieldId: number) => {
      const result = fields.find((f: TField) => f.id === fieldId);
      if (result) {
        return { id: result.id, fieldType: result.fieldType };
      }
      return null;
    };
    const fieldWithFieldType = allFields?.map((field: number) =>
      getFieldType(field)
    );
    const excludeReference = fieldWithFieldType?.filter(
      (field: any) => field?.fieldType !== EFieldType.REFERENCE
    );
    return excludeReference?.map((field: any) => field?.id);
  }
};

export const moveElementInArray = (array: [any], from: number, to: number) => {
  const clonedArray = Object.assign([], array);
  const f = clonedArray.splice(from, 1)[0];
  clonedArray.splice(to, 0, f);
  return clonedArray;
};

export const isNewObject = (object) => object?.id === NEW_OBJECT_ID;

// Helper: build ordered unique list of field IDs for a set of typeIds
export const collectOrderedFieldIds = (
  typeIds: number[],
  objectTypesMap: TRootState['objectTypes']['map'],
  globalFieldOrder: { id: number }[]
): number[] => {
  const raw: number[] = [];
  for (const tid of typeIds) {
    const type = objectTypesMap[tid];
    if (!type) continue;
    if (Array.isArray(type.parentFields)) raw.push(...type.parentFields);
    if (Array.isArray(type.fields)) raw.push(...type.fields);
  }
  // Deduplicate preserving first occurrence order, then sort by global field order
  const seen = new Set<number>();
  const unique: number[] = [];
  for (const id of raw) {
    if (id != null && !seen.has(id)) {
      seen.add(id);
      unique.push(id);
    }
  }
  return unique.sort((a, b) => {
    const ia = globalFieldOrder.findIndex((f) => f.id === a);
    const ib = globalFieldOrder.findIndex((f) => f.id === b);
    return ia - ib;
  });
};

// Helper: produce merged field array preserving existing values when possible
export const mergeFieldsForTypes = (
  orderedFieldIds: number[],
  currentFields: TTEObject['fields'] | undefined,
  originalFields: TTEObject['fields'] | undefined,
  fieldsMap: Record<number, TField>
) => {
  const currentMap = new Map((currentFields ?? []).map((f) => [f.fieldId, f]));
  const originalMap = new Map(
    (originalFields ?? []).map((f) => [f.fieldId, f])
  );
  return orderedFieldIds
    .filter((id) => id && isFieldSaveable(fieldsMap[id]))
    .map((id) => {
      const existing = currentMap.get(id) || originalMap.get(id);
      return { fieldId: id, values: existing ? existing.values : [] };
    });
};
